import { Prisma } from '@prisma/client';
import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_RESOURCES,
  MN_SUPPLIERS,
  MODULE,
} from '../common';
import * as z from 'zod';
import type { KeyValue } from '@jobcost-monorepo/utils';
import DailyRevenue from '../ui/CustomComponents/DailyRevenue';
import DailyCost from '../ui/CustomComponents/DailyCost';
import HookComponentResource from '../ui/CustomHookComponent/HookComponentResource';

type Resource = Prisma.$ResourcePayload['scalars'];

export type IResource = Resource &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    supplier: KeyValue;
  };

export interface IResourceFetchParams extends IListFetchParams {
  supplierId?: number;
  isEmployee?: boolean;
  hasCV?: boolean;
}

class ResourceRepository extends BaseRepository<
  IResource,
  IResourceFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['lastName', 'firstName'],
    };
    this.gridFieldsOptions = [
      {
        visible: false,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'firstName',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'lastName',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'hourCost',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'currency',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'hourRevenue',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'currency',
      },
      {
        visible: true,
        position: 5,
        linkedField: 'supplier.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'supplierId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        position: { x: 0, y: 1 },
        linkedField: 'isEmployee',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        position: { x: 0, y: 2 },
        linkedField: 'hasCV',
        width: 'w-full',
        formatType: 'boolean',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'lastName',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'firstName',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'address',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 1 },
        linkedField: 'city',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'postalCode',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 2 },
        linkedField: 'state',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 2 },
        linkedField: 'country',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'fiscalCode',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 4 },
        linkedField: 'annualCost',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 4 },
        linkedField: 'hourCost',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 4 },
        linkedField: 'dailyCost',
        formatType: 'custom',
        customComponent: DailyCost,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 5 },
        linkedField: 'hourRevenue',
        type: 'number',
        width: 'w-full',
        formatType: 'currency',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 5 },
        linkedField: 'dailyRevenue',
        formatType: 'custom',
        customComponent: DailyRevenue,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 6 },
        linkedField: 'supplierId',
        linkedFieldForDefaultValue: 'supplier.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 7 },
        linkedField: 'curriculumVitae',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 8 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      lastName: z
        .string({
          required_error: 'fieldRequired',
          invalid_type_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      firstName: z
        .string({
          required_error: 'fieldRequired',
          invalid_type_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(30, { message: 'max_30' }),
      address: z
        .string()
        .min(2, { message: 'min_2' })
        .max(50, { message: 'max_50' })
        .nullable()
        .optional(),
      postalCode: z
        .string()
        .min(5, { message: 'min_5' })
        .max(10, { message: 'max_10' })
        .nullable()
        .optional(),
      city: z
        .string()
        .min(2, { message: 'min_2' })
        .max(50, { message: 'max_50' })
        .nullable()
        .optional(),
      state: z
        .string()
        .min(2, { message: 'min_2' })
        .max(50, { message: 'max_50' })
        .nullable()
        .optional(),
      country: z
        .string()
        .min(2, { message: 'min_2' })
        .max(2, { message: 'max_2' })
        .nullable()
        .optional(),
      fiscalCode: z
        .string()
        .min(16, { message: 'invalidFiscalCode' })
        .max(16, { message: 'invalidFiscalCode' })
        .nullable()
        .optional(),
      annualCost: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .nullable()
        .optional(),
      hourCost: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .nullable()
        .optional(),
      hourRevenue: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nonnegative({ message: 'nonNegativeNumber' })
        .min(0, { message: 'min_num_0' })
        .nullable()
        .optional(),
      supplierId: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nullable()
        .optional(),
      curriculumVitae: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });

    this.hook = HookComponentResource;
  }
}

export default ResourceRepository;
